/**
 * platform-ai-keys-page.tsx — Platform → AI → Khoá API của eCDP (`/platform/ai/keys`).
 * Key nền tảng dùng chung cho mọi tổ chức chọn "AI của eCDP". Server kiểm key trước khi lưu và
 * không bao giờ trả key — chỉ gợi ý 4 ký tự cuối.
 */
import { useState } from 'react'
import { toast } from 'sonner'
import { KeyRound, Loader2, Plus, RefreshCw, Trash2 } from 'lucide-react'
import { PageHeader } from '@/components/shared/page-header'
import { DataTable, type Column } from '@/components/shared/data-table'
import { EmptyState, ErrorState } from '@/components/shared/feedback'
import { AiProviderOption } from '@/components/shared/ai-provider-option'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/misc'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { SUPPORTED_AI_PROVIDERS } from '@/lib/ai-providers'
import { aiErrorMessage } from '@/lib/ai-credits'
import {
  useCreatePlatformAiKey, useDeletePlatformAiKey, usePlatformAiKeys, useUpdatePlatformAiKey, useVerifyPlatformAiKey,
  type PlatformAiKey,
} from '@/hooks/use-platform-ai'

export const PROVIDER_NAME: Record<string, string> = {
  openai: 'ChatGPT (OpenAI)',
  anthropic: 'Claude (Anthropic)',
  gemini: 'Gemini (Google)',
  xai: 'Grok (xAI)',
  deepseek: 'DeepSeek',
}
export const providerName = (id: string) => PROVIDER_NAME[id] ?? id
const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString('vi-VN', { dateStyle: 'short', timeStyle: 'short' }) : '—')

export function PlatformAiKeysPage() {
  const { data, isLoading, isError } = usePlatformAiKeys()
  const update = useUpdatePlatformAiKey()
  const del = useDeletePlatformAiKey()
  const verify = useVerifyPlatformAiKey()
  const [adding, setAdding] = useState(false)
  const [replacing, setReplacing] = useState<PlatformAiKey | null>(null)

  const onErr = (e: unknown) => toast.error(aiErrorMessage(e))

  const columns: Column<PlatformAiKey>[] = [
    { key: 'provider', header: 'Nhà cung cấp', cell: (r) => <AiProviderOption id={r.provider} name={providerName(r.provider)} /> },
    {
      key: 'label', header: 'Tên / key',
      cell: (r) => (
        <div>
          <p className="font-medium">{r.label || 'Không tên'}</p>
          <p className="font-mono text-xs text-muted-foreground">{r.hint ?? '••••'}</p>
        </div>
      ),
    },
    { key: 'priority', header: 'Ưu tiên', align: 'right', cell: (r) => r.priority },
    {
      key: 'status', header: 'Trạng thái',
      cell: (r) => (
        <div className="space-y-0.5">
          {r.lastError
            ? <Badge variant="destructive">Key lỗi</Badge>
            : r.status === 'active' ? <Badge variant="success">Đang dùng</Badge> : <Badge variant="secondary">Đã tắt</Badge>}
          {r.lastError && <p className="max-w-56 truncate text-xs text-destructive" title={r.lastError}>{r.lastError}</p>}
        </div>
      ),
    },
    {
      key: 'times', header: 'Kiểm tra / dùng gần nhất',
      cell: (r) => <span className="text-xs text-muted-foreground">{fmt(r.lastVerifiedAt)} · {fmt(r.lastUsedAt)}</span>,
    },
    {
      key: 'actions', header: '', align: 'right',
      cell: (r) => (
        <div className="flex items-center justify-end gap-1.5">
          <Switch
            checked={r.status === 'active'}
            aria-label="Bật / tắt key"
            onCheckedChange={(v) => update.mutate({ id: r.id, status: v ? 'active' : 'disabled' }, {
              onSuccess: () => toast.success(v ? 'Đã bật key' : 'Đã tắt key'), onError: onErr,
            })}
          />
          <Button
            size="sm" variant="outline" disabled={verify.isPending}
            onClick={() => verify.mutate(r.id, {
              onSuccess: (res) => (res?.ok === false ? toast.error(res.error || 'Key không hợp lệ') : toast.success('Key hoạt động bình thường')),
              onError: onErr,
            })}
          >
            <RefreshCw className="h-3.5 w-3.5" /> Kiểm tra
          </Button>
          <Button size="sm" variant="outline" onClick={() => setReplacing(r)}>Thay key</Button>
          <Button
            size="icon" variant="ghost" title="Xoá key"
            onClick={() => {
              if (!window.confirm(`Xoá key “${r.label || r.hint || r.provider}”? Tổ chức dùng AI của eCDP sẽ chuyển sang key khác cùng nhà cung cấp (nếu có).`)) return
              del.mutate(r.id, { onSuccess: () => toast.success('Đã xoá key'), onError: onErr })
            }}
          >
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      ),
    },
  ]

  const missingProviders = data ? SUPPORTED_AI_PROVIDERS.filter((p) => !data.some((k) => k.provider === p && k.status === 'active')) : []

  return (
    <div className="space-y-4">
      <PageHeader
        title="Khoá API của eCDP"
        description="Key nền tảng dùng chung cho mọi tổ chức chọn “AI của eCDP”. Chỉ super-admin thấy; key được kiểm tra trước khi lưu và không bao giờ hiện lại."
        actions={data !== null && <Button onClick={() => setAdding(true)}><Plus className="h-4 w-4" /> Thêm key</Button>}
      />
      {isError ? (
        <ErrorState />
      ) : data === null ? (
        <EmptyState icon={KeyRound} title="Máy chủ chưa hỗ trợ khoá API của eCDP" description="Tính năng AI hai chế độ đang được triển khai phía máy chủ." />
      ) : (
        <>
          {missingProviders.length > 0 && !isLoading && (
            <p className="rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-sm">
              Chưa có key đang dùng cho: {missingProviders.map(providerName).join(', ')} — tổ chức chọn AI của eCDP cho các nhà cung cấp này sẽ báo “eCDP tạm dừng”.
            </p>
          )}
          <DataTable columns={columns} rows={data ?? []} rowKey={(r) => r.id} loading={isLoading} emptyTitle="Chưa có key nền tảng nào" />
        </>
      )}
      <AddKeyDialog open={adding} onClose={() => setAdding(false)} />
      <ReplaceKeyDialog target={replacing} onClose={() => setReplacing(null)} />
    </div>
  )
}

function AddKeyDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const create = useCreatePlatformAiKey()
  const [provider, setProvider] = useState<string>('openai')
  const [apiKey, setApiKey] = useState('')
  const [label, setLabel] = useState('')
  const [priority, setPriority] = useState('0')
  const close = () => { setApiKey(''); setLabel(''); setPriority('0'); onClose() }
  const save = () => {
    const pr = Number(priority)
    if (!apiKey.trim()) return void toast.error('Nhập API key')
    if (!Number.isInteger(pr)) return void toast.error('Ưu tiên phải là số nguyên')
    create.mutate(
      { provider, apiKey: apiKey.trim(), label: label.trim() || undefined, priority: pr },
      { onSuccess: () => { toast.success('Đã kiểm tra & lưu key'); close() }, onError: (e) => toast.error(aiErrorMessage(e)) },
    )
  }
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close() }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Thêm key nền tảng</DialogTitle>
          <DialogDescription>Key được gọi thử trước khi lưu. Nhiều key cùng nhà cung cấp: ưu tiên nhỏ hơn dùng trước.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Nhà cung cấp</Label>
            <Select value={provider} onValueChange={setProvider}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {SUPPORTED_AI_PROVIDERS.map((p) => (
                  <SelectItem key={p} value={p}><AiProviderOption id={p} name={providerName(p)} /></SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>API key</Label>
            <Input type="password" autoComplete="off" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="Dán API key..." />
          </div>
          <div className="grid grid-cols-[1fr_120px] gap-3">
            <div className="space-y-1.5">
              <Label>Tên gợi nhớ</Label>
              <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="vd: OpenAI chính" maxLength={80} />
            </div>
            <div className="space-y-1.5">
              <Label>Ưu tiên</Label>
              <Input type="number" value={priority} onChange={(e) => setPriority(e.target.value)} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={close}>Huỷ</Button>
          <Button onClick={save} disabled={create.isPending}>
            {create.isPending && <Loader2 className="animate-spin" />} Kiểm tra &amp; lưu
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function ReplaceKeyDialog({ target, onClose }: { target: PlatformAiKey | null; onClose: () => void }) {
  const update = useUpdatePlatformAiKey()
  const [apiKey, setApiKey] = useState('')
  const [label, setLabel] = useState('')
  const [priority, setPriority] = useState('')
  const [loadedFor, setLoadedFor] = useState<string | null>(null)
  if (target && loadedFor !== target.id) {
    setLoadedFor(target.id)
    setApiKey('')
    setLabel(target.label ?? '')
    setPriority(String(target.priority))
  }
  const close = () => { setLoadedFor(null); onClose() }
  const save = () => {
    if (!target) return
    const pr = Number(priority)
    if (!Number.isInteger(pr)) return void toast.error('Ưu tiên phải là số nguyên')
    update.mutate(
      { id: target.id, label: label.trim(), priority: pr, ...(apiKey.trim() ? { apiKey: apiKey.trim() } : {}) },
      { onSuccess: () => { toast.success('Đã cập nhật key'); close() }, onError: (e) => toast.error(aiErrorMessage(e)) },
    )
  }
  return (
    <Dialog open={!!target} onOpenChange={(o) => { if (!o) close() }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Sửa key {target ? providerName(target.provider) : ''}</DialogTitle>
          <DialogDescription>Để trống ô API key nếu chỉ đổi tên / ưu tiên.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>API key mới</Label>
            <Input type="password" autoComplete="off" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder={target?.hint ?? 'Dán API key...'} />
          </div>
          <div className="grid grid-cols-[1fr_120px] gap-3">
            <div className="space-y-1.5">
              <Label>Tên gợi nhớ</Label>
              <Input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={80} />
            </div>
            <div className="space-y-1.5">
              <Label>Ưu tiên</Label>
              <Input type="number" value={priority} onChange={(e) => setPriority(e.target.value)} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={close}>Huỷ</Button>
          <Button onClick={save} disabled={update.isPending}>
            {update.isPending && <Loader2 className="animate-spin" />} Lưu
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
